import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MIN_SEGMENT_SIZE,
  RELATIONSHIP_ORDER,
  type ParticipantDetailDto,
  type RaterRelationship,
} from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { useCan } from '../../stores/auth.store.ts';
import { fetchParticipant } from '../../services/dashboard.service.ts';
import { InvitationCard } from './InvitationCard.tsx';
import { RaterGroup } from './RaterGroup.tsx';

/**
 * One evaluated person.
 *
 * Today this is their progress: who was asked, who answered, and what their
 * report would look like if the cycle closed now. The report itself is the
 * next thing to be built — and it will only appear here once a coach has
 * published it, which is a rule, not a screen state.
 */
export const ParticipantDetailPage = () => {
  const { t } = useI18n();
  const canReadReport = useCan('report:read:all') || useCan('report:read:redacted');
  const { participantId } = useParams<{ participantId: string }>();

  const [detail, setDetail] = useState<ParticipantDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!participantId) return;
    fetchParticipant(participantId)
      .then(setDetail)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  if (error) return <Centered>{error}</Centered>;
  if (!detail) return <Centered>{t('common.loading')}</Centered>;

  const { participant, raters } = detail;

  // Grouped in the order the report presents relationships.
  const groups = (RELATIONSHIP_ORDER as readonly RaterRelationship[])
    .concat('SELF')
    .map((relationship) => ({
      relationship,
      raters: raters.filter((rater) => rater.relationship === relationship),
    }))
    .filter((group) => group.raters.length > 0);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <Link
        to="/participantes"
        className="font-mono text-[10px] tracking-[0.12em] text-soft uppercase hover:text-teal"
      >
        ← {t('participants.title')}
      </Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">
            {participant.fullName}
          </h1>
          <p className="mt-0.5 text-[13.5px] text-soft">
            {participant.jobTitle ?? t(`roleLevels.${participant.roleLevel}`)}
            {participant.area && ` · ${participant.area}`}
            {` · ${t(`roleLevels.${participant.roleLevel}`)}`}
          </p>
        </div>
        {participant.keyLeader && (
          <span className="rounded-full border border-tint-line bg-tint px-2.5 py-1 font-mono text-[9.5px] tracking-[0.12em] text-teal-deep uppercase">
            {t('dashboard.keyLeader')}
          </span>
        )}
      </header>

      <section className="mt-6 grid gap-4 sm:grid-cols-[1fr_1fr]">
        <div className="rounded-2xl border border-line bg-paper px-4 py-4 shadow-card">
          <h2 className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
            {t('participants.whoAnswered')}
          </h2>
          <div className="mt-3 flex flex-col">
            {groups.map((group) => (
              <RaterGroup
                key={group.relationship}
                relationship={group.relationship}
                raters={group.raters}
              />
            ))}
          </div>
          <p className="mt-3.5 border-t border-line-soft pt-2.5 text-[11.5px] text-faint">
            {t('participants.thresholdHelpFull', { min: MIN_SEGMENT_SIZE })}{' '}
            {t('participants.expandHelp')}
          </p>
        </div>

        <div className="rounded-2xl border border-line bg-paper px-4 py-4 shadow-card">
          <h2 className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
            {t('dashboard.colOutlook')}
          </h2>

          {participant.reportBlocked ? (
            <p className="mt-3 flex items-start gap-2 text-[14px]">
              <span aria-hidden className="mt-1 text-[10px] text-[var(--color-critical)]">
                ●
              </span>
              <span>{t('participants.outlookBlockedHelp')}</span>
            </p>
          ) : participant.thinSegments.length > 0 ? (
            <p className="mt-3 flex items-start gap-2 text-[14px]">
              <span aria-hidden className="mt-1 text-[10px] text-[var(--color-warning)]">
                ▲
              </span>
              <span>
                {t('participants.outlookAtRiskHelp', {
                  segments: participant.thinSegments
                    .map((segment) => t(`relationships.${segment}`))
                    .join(', '),
                })}
              </span>
            </p>
          ) : participant.globalOnly ? (
            <p className="mt-3 text-[14px] text-soft">{t('participants.outlookGlobalHelp')}</p>
          ) : (
            <p className="mt-3 text-[14px] text-soft">
              {t('participants.outlookSegmentedHelp')}
            </p>
          )}

          <p className="mt-4 border-t border-line-soft pt-3 text-[13px] text-soft">
            {t('participants.selfAssessment')}:{' '}
            <b className={participant.selfAssessmentDone ? 'text-teal-deep' : 'text-faint'}>
              {participant.selfAssessmentDone
                ? t('dashboard.done')
                : t('dashboard.pending')}
            </b>
          </p>
        </div>
      </section>

      <InvitationCard
        personId={detail.personId}
        detail={detail}
        onChange={(changes) =>
          setDetail((current) => (current ? { ...current, ...changes } : current))
        }
      />

      {/* The report itself, for whoever may read one.

          P&O see this page and not this section: they run the cycle without
          receiving its findings. Every rater was promised their answers would
          be combined before anybody read them, and the shortest way to keep
          that promise is for the client's own staff not to be readers. */}
      {canReadReport && (
        <section className="mt-6">
          <h2 className="mb-2.5 flex items-center gap-2 font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
            {t('report.title')}
            <span className="h-px flex-1 bg-line-soft" />
          </h2>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-paper px-4 py-4 shadow-card">
            <span>
              <span className="block text-[14px]">
                {participant.reportStatus
                  ? t(`reportStatus.${participant.reportStatus}`)
                  : t('participants.reportNotApproved')}
              </span>
              <span className="mt-0.5 block text-[12.5px] text-faint">
                {t('participants.reportNotApprovedHelp')}
              </span>
            </span>
            <Link
              to={`/participantes/${participant.participantId}/reporte`}
              className="shrink-0 rounded-xl bg-teal px-4 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep"
            >
              {t('participants.openReport')}
            </Link>
          </div>
        </section>
      )}

      <p className="mt-8 border-t border-line-soft pt-4 text-xs text-faint">
        {t('dashboard.privacyFooter')}
      </p>
    </div>
  );
};
